import { useContext } from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { UseCouponContextProps, CartCoupon } from '../types';
import { CouponProviderContext } from '../CouponContextProvider';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { ShopifyStorefrontClient } from '../../../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from '../../../utils/cart/transformStorefrontCartToJbCart';
import { drawCart, setCartLoading } from '../../../utils/legacyCart';
import { ShopifyCustomerClient } from '../../../templates/components/customers/api/shopifyCustomer/ShopifyCustomerClient';
import { getFilteredCoupons, handleExclusions } from '../utils';
import { useQuery } from 'react-query';
import { useCouponTracking } from '../../../hooks/useCouponTracking/useCouponTracking';

const storefrontClient = new ShopifyStorefrontClient();
const shopifyCustomerClient = new ShopifyCustomerClient();

export const useCouponContext = ({ location, product }: UseCouponContextProps) => {
  const context = useContext(CouponProviderContext);
  const { cart } = useCartContext();

  if (!context) throw new Error('useCouponContext must be used within a CouponProvider');

  const {
    isCouponSurfacingEnabled,
    isCouponSurfacingEnabledForAttachSlideout,
    isCouponSurfacingEnabledForCart,
    coupons,
    isLoading,
    isModalOpen,
    selectedCoupon,
    isFetched,
    isActive,
    selectedCouponLocation,
    setCoupons,
    setSelectedCoupon,
    setIsModalOpen,
    setIsLoading,
    setSelectedCouponLocation,
  } = context;

  const handleToggleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen);
  };

  const { trackCouponEvent } = useCouponTracking(selectedCoupon);

  const handleSelectCoupon = (couponCode: string) => {
    const matchingCoupon = coupons.find(coupon => coupon.couponCode === couponCode);

    setIsModalOpen(true);
    if (matchingCoupon) {
      setSelectedCoupon(matchingCoupon);
    }
    trackCouponEvent({ type: 'click_coupon_redeem', location, coupon: matchingCoupon });
    trackCouponEvent({ type: 'view_popup', location, coupon: matchingCoupon });
    if (location) setSelectedCouponLocation(location);
  };

  const handleDeselectCoupon = () => {
    setSelectedCoupon(null);
    setIsModalOpen(false);
  };

  const handleApplyCoupon = async (shouldCloseModal: boolean = false, inputCouponCode: string = '') => {
    if (!selectedCoupon && !inputCouponCode) return;
    inputCouponCode ? setCartLoading(true) : setIsLoading(true);
    !inputCouponCode &&
      trackCouponEvent({
        type: 'click_coupon_cta',
        label: 'Apply coupon to My Cart',
        location: selectedCouponLocation ?? undefined,
      });

    const couponCode = inputCouponCode || selectedCoupon?.couponCode || '';

    const updatedCart = await storefrontClient.setCartAttributes(cart?.attributes, {
      _coupons: JSON.stringify([
        ...(tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons || '') || []),
        {
          externalId: selectedCoupon?.couponExternalId,
          code: couponCode,
          coupon: selectedCoupon?.couponReference,
          action: inputCouponCode ? 'manual' : 'digital',
          location: selectedCouponLocation,
          status: 'loading',
        },
      ]),
    });

    const transformedCart = transformStorefrontCartToJbCart(updatedCart);
    const cartResponse = await drawCart(transformedCart);
    const isCouponValid = !cartResponse?.UnusedCoupons?.some(unusedCoupon => unusedCoupon.Reference === couponCode);
    setCoupons(getFilteredCoupons(coupons, [{ code: couponCode, status: 'loading' }]));

    trackCouponEvent({
      type: 'coupon_used',
      location: selectedCouponLocation ?? undefined,
      couponStatusInCart: { is_valid: isCouponValid ? 'valid_coupon' : 'invalid_coupon' },
    });
    inputCouponCode ? setCartLoading(false) : setIsLoading(false);
    if (shouldCloseModal) {
      handleDeselectCoupon();
    }
  };

  const { data: includedCoupons = [] } = useQuery({
    enabled: isActive && Boolean(product),
    queryKey: ['getExclusions', coupons.map(coupon => coupon.couponExternalId?.toString())],
    refetchOnWindowFocus: false,
    queryFn: () => handleExclusions(coupons, product),
  });

  const couponsWithExclusions = coupons.filter(coupon => includedCoupons.find(c => c.couponCode === coupon.couponCode));

  const handleViewCoupons = async () => {
    const shopifyCustomerId = window.Jbhifi?.shopifyCustomerId;
    if (!coupons.length) return;
    const simpleCoupons = coupons
      .filter(coupon => coupon.isViewed !== true)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ content, ...simpleCoupon }) => simpleCoupon);

    const result = await shopifyCustomerClient.updateCoupons({
      customerId: shopifyCustomerId,
      coupons: simpleCoupons,
    });

    if (result?.statusCode === 200) {
      const viewedCoupons = coupons.map(coupon => (coupon.isViewed ? coupon : { ...coupon, isViewed: true }));
      setCoupons(viewedCoupons);
    } else {
      console.error('Failed to update coupons', result);
    }
  };

  const handleRemoveCoupon = async (couponCode: string, isGuestCart: boolean = false) => {
    isGuestCart && setCartLoading(true);
    const updatedCoupons = (tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons) || []).filter(
      coupon => coupon.code !== couponCode
    );
    const updatedCart = await storefrontClient.setCartAttributes(cart?.attributes, {
      _coupons: JSON.stringify(updatedCoupons),
    });

    const transformedCart = transformStorefrontCartToJbCart(updatedCart);
    await drawCart(transformedCart);
    setCoupons(getFilteredCoupons(coupons, updatedCoupons));

    const couponRemoved = coupons.find(c => c.couponCode === couponCode);

    trackCouponEvent({
      type: 'coupon_remove',
      location: selectedCouponLocation ?? undefined,
      coupon: couponRemoved,
    });
    isGuestCart && setCartLoading(false);
  };

  const viewedCouponsCount = coupons.reduce((count, coupon) => {
    return !coupon.isViewed ? count + 1 : count;
  }, 0);

  const shouldShowCoupons = () => {
    switch (location) {
      case 'cart':
        return isCouponSurfacingEnabledForCart;
      case 'slideout':
        return isCouponSurfacingEnabledForAttachSlideout;
      default:
        return isCouponSurfacingEnabled;
    }
  };

  return {
    isCouponSurfacingEnabled: shouldShowCoupons(),
    coupons,
    couponsWithExclusions,
    isLoading,
    isApplyingCoupon: false,
    isRemovingCoupon: false,
    isModalOpen,
    selectedCoupon,
    newCouponCount: viewedCouponsCount,
    isFetched,
    isActive,
    handleToggleIsModalOpen,
    handleSelectCoupon,
    handleApplyCoupon,
    handleRemoveCoupon,
    handleDeselectCoupon,
    handleViewCoupons,
  };
};
